import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { AdminRefreshButton } from '../../components/primitives/admin-refresh-button.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { formatPaise, formatDate } from '../../lib/format.js';
import { useAdminCustomers } from '../hooks/use-customers.js';
import { USER_STATUSES, type AdminCustomerSummary, type CustomerListParams } from '../types/customers.js';

const PAGE_SIZE = 20;

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  locked: 'warning',
  suspended: 'danger',
  disabled: 'neutral',
  deleted: 'neutral',
};

export function CustomersDirectoryPage() {
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);

  const params: CustomerListParams = useMemo(
    () => ({
      search: search || undefined,
      status: status ? [status as never] : undefined,
      sortField: 'createdAt',
      sortDirection: 'desc',
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, status, page],
  );

  const { data, isLoading, isError, refetch, isFetching } = useAdminCustomers(params);
  const rows = data ?? [];

  const columns: AdminDataTableColumn<AdminCustomerSummary>[] = [
    {
      key: 'customer',
      header: 'Customer',
      render: (row) => (
        <Link to={`${ROUTE_NAMES.adminCustomers}/${row.id}`} className="font-medium text-gray-900 hover:underline">
          {row.fullName ?? row.email}
        </Link>
      ),
    },
    { key: 'email', header: 'Email', render: (row) => row.email },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <AdminStatusBadge label={row.status} tone={STATUS_TONE[row.status] ?? 'neutral'} />,
    },
    { key: 'orderCount', header: 'Orders', render: (row) => row.orderCount },
    { key: 'totalSpend', header: 'Total spend', render: (row) => formatPaise(row.totalSpend.amountPaise) },
    { key: 'lastOrderAt', header: 'Last order', render: (row) => (row.lastOrderAt ? formatDate(row.lastOrderAt) : '—') },
    { key: 'joinedAt', header: 'Joined', render: (row) => formatDate(row.joinedAt) },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Customers"
        description="Customer directory and support profiles."
        breadcrumbs={[{ label: 'Customers' }]}
        actions={<AdminRefreshButton onRefresh={() => void refetch()} isRefreshing={isFetching} />}
      />

      <AdminSectionCard title="All customers" className="p-4 sm:p-5">
        <AdminFilterBar>
          <AdminSearchInput
            value={searchInput}
            onChange={(value) => {
              setSearchInput(value);
              setPage(0);
            }}
            placeholder="Search name, email, or phone…"
            label="Search customers"
          />
          <AdminSelect
            label="Account status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(0);
            }}
            allLabel="All statuses"
            options={USER_STATUSES.map((s) => ({ value: s, label: s }))}
          />
        </AdminFilterBar>

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No customers match these filters"
        />

        <nav aria-label="Pagination" className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </button>
          <span>Page {page + 1}</span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < PAGE_SIZE}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </nav>
      </AdminSectionCard>
    </AdminContent>
  );
}
